import { StyleSheet, type ViewStyle, type ImageStyle } from 'react-native';
import {
  MONKEY_STAGE,
  MONKEY_LAYOUT,
  BREATHE_PIVOT,
  HEAD_PIVOT,
  ARM_REST_DEG,
  pivotDelta,
} from './AnimatedMonkey.constants';

/**
 * Pivots are applied as explicit translate→ops→translate-back sandwiches instead of
 * `transformOrigin`: native Android drops the origin when `Animated` replaces the transform
 * per frame, which broke the arm wave and blink placement on device (web was fine).
 */
export const MONKEY_PIVOT_DELTAS = {
  /** Breathe: whole group scales about the body's bottom-centre. */
  group: pivotDelta(BREATHE_PIVOT, MONKEY_STAGE),
  /** Head sway / wave tilt: rocks about the neck join. */
  head: pivotDelta(HEAD_PIVOT, MONKEY_STAGE),
  /** Arm wave: rotates about the shoulder. */
  arm: pivotDelta(MONKEY_LAYOUT.arm.pivot, MONKEY_LAYOUT.arm.size),
  /** Lid blinks: scale about each lid's hinge (the crease over the eye). */
  lidL: pivotDelta(MONKEY_LAYOUT.lidL.pivot, MONKEY_LAYOUT.lidL.size),
  lidR: pivotDelta(MONKEY_LAYOUT.lidR.pivot, MONKEY_LAYOUT.lidR.size),
} as const;

/** Wrap transform ops so they apply about the pivot (RN transforms pivot about the centre). */
export const withPivot = <T,>(
  delta: { dx: number; dy: number },
  ops: readonly T[],
): (T | { translateX: number } | { translateY: number })[] => [
  { translateX: delta.dx },
  { translateY: delta.dy },
  ...ops,
  { translateX: -delta.dx },
  { translateY: -delta.dy },
];

/**
 * Frame + stage scale depend on the runtime `size` prop; the stage is the calibrated
 * 1024-unit canvas scaled down to fit and clipped to a square frame (matches the rig).
 */
export const getAnimatedMonkeyFrameStyles = (
  size: number,
): { frame: ViewStyle; stage: ViewStyle } => ({
  frame: {
    width: size,
    height: size,
    overflow: 'hidden',
    position: 'relative',
  },
  stage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: MONKEY_STAGE,
    height: MONKEY_STAGE,
    // Scale about the top-left corner (pivot 0,0) via the same sandwich used everywhere else.
    transform: withPivot({ dx: -MONKEY_STAGE / 2, dy: -MONKEY_STAGE / 2 }, [
      { scale: size / MONKEY_STAGE },
    ]) as ViewStyle['transform'],
  },
});

const rect = (part: { left: number; top: number; size: number }): ViewStyle => ({
  position: 'absolute',
  left: part.left,
  top: part.top,
  width: part.size,
  height: part.size,
});

const FILL: ImageStyle = { width: '100%', height: '100%' };

/** A full-stage layer used to group the head + face so they sway / tilt as one unit. */
const stageLayer: ViewStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: MONKEY_STAGE,
  height: MONKEY_STAGE,
};

export const monkeyStyles = StyleSheet.create({
  /** The breathing group holds every part; scaleY pivots at the body's bottom-centre. */
  group: stageLayer,
  /** Head + pupils + lids; sway/wave-tilt pivot at the neck so the head rocks, not spins. */
  headGroup: stageLayer,
  fill: FILL,
  body: rect(MONKEY_LAYOUT.body),
  arm: {
    ...rect(MONKEY_LAYOUT.arm),
    transform: withPivot(MONKEY_PIVOT_DELTAS.arm, [
      { rotate: ARM_REST_DEG },
    ]) as ViewStyle['transform'],
  },
  head: rect(MONKEY_LAYOUT.head),
  lidL: {
    ...rect(MONKEY_LAYOUT.lidL),
    transform: withPivot(MONKEY_PIVOT_DELTAS.lidL, [{ scaleY: 0 }]) as ViewStyle['transform'],
  },
  lidR: {
    ...rect(MONKEY_LAYOUT.lidR),
    transform: withPivot(MONKEY_PIVOT_DELTAS.lidR, [{ scaleY: 0 }]) as ViewStyle['transform'],
  },
});
